import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Archive, Check, ExternalLink, RotateCcw, Star } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  StatusBadge,
  type StatusBadgeVariant,
} from "@/components/shared/StatusBadge";
import { formatDisplayDate } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";
import {
  deriveDisplayStatus,
  getAdminFeedbackDetail,
  updateAdminRequestFeedbackStatus,
  type DisplayStatus,
  type RequestFeedbackRequestType,
  type RequestFeedbackStatus,
} from "@/services/requestFeedbackService";
import { requestTypeLabel } from "./request-type-label";

function npsBucket(score: number): "promoter" | "passive" | "detractor" {
  if (score >= 9) return "promoter";
  if (score >= 7) return "passive";
  return "detractor";
}

function StarRow({ value }: { value: number | null }): JSX.Element {
  if (value == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={cn(
            "h-5 w-5",
            n <= value
              ? "fill-yellow-400 text-yellow-400"
              : "fill-transparent text-muted-foreground"
          )}
        />
      ))}
      <span className="ml-2 text-sm text-muted-foreground">{value}/5</span>
    </span>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-3 first:pt-0 last:pb-0 border-b border-border last:border-0">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

export function RequestRatingDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["platform", "request-feedback", "detail", id],
    queryFn: () => getAdminFeedbackDetail(id!),
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ status }: { status: RequestFeedbackStatus }) =>
      updateAdminRequestFeedbackStatus(id!, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["platform", "request-feedback"],
      });
    },
    onError: () => {
      toast.error(t("ratings.page.statusUpdateError"));
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 p-4 md:p-8">
        <PageHeader
          title={t("ratings.detail.notFoundTitle")}
          description={t("ratings.detail.notFoundDescription")}
          onBack={() => navigate("/ratings")}
        />
      </div>
    );
  }

  const typeLabel = requestTypeLabel(t, data.requestType);

  const displayStatusBadge = (display: DisplayStatus): JSX.Element => {
    const variant: StatusBadgeVariant =
      display === "submitted"
        ? "success"
        : display === "sent"
          ? "warning"
          : display === "queued"
            ? "info"
            : display === "reviewed"
              ? "info"
              : "softDanger";
    const label =
      display === "queued"
        ? t("ratings.page.statusQueued")
        : display === "sent"
          ? t("ratings.page.statusSent")
          : display === "submitted"
            ? t("ratings.page.statusSubmitted")
            : display === "reviewed"
              ? t("ratings.page.statusReviewed")
              : t("ratings.page.statusArchived");
    return <StatusBadge variant={variant}>{label}</StatusBadge>;
  };

  const bucket = data.npsScore != null ? npsBucket(data.npsScore) : null;
  const bucketLabel =
    bucket === "promoter"
      ? t("ratings.nps.promoter")
      : bucket === "passive"
        ? t("ratings.nps.passive")
        : bucket === "detractor"
          ? t("ratings.nps.detractor")
          : null;
  const bucketClass =
    bucket === "promoter"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      : bucket === "passive"
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
        : "bg-red-500/15 text-red-700 dark:text-red-300";

  const requestHref =
    data.requestType === "accountant"
      ? `/requests/${data.requestId}`
      : `/requests/${data.requestId}`;

  return (
    <div className="space-y-4 p-4 md:p-8">
      <PageHeader
        title={t("ratings.detail.title")}
        description={
          data.consumerName ||
          data.consumerEmail ||
          t("ratings.detail.title")
        }
        onBack={() => navigate("/ratings")}
        actions={
          <div className="flex flex-wrap gap-2">
            {data.status === "submitted" && (
              <Button
                onClick={() =>
                  updateStatusMutation.mutate({ status: "reviewed" })
                }
                disabled={updateStatusMutation.isPending}
              >
                <Check className="mr-2 h-4 w-4" />
                {t("ratings.page.markReviewed")}
              </Button>
            )}
            {(data.status === "submitted" || data.status === "reviewed") && (
              <Button
                variant="outline"
                onClick={() =>
                  updateStatusMutation.mutate({ status: "archived" })
                }
                disabled={updateStatusMutation.isPending}
              >
                <Archive className="mr-2 h-4 w-4" />
                {t("ratings.page.markArchived")}
              </Button>
            )}
            {(data.status === "reviewed" || data.status === "archived") && (
              <Button
                variant="outline"
                onClick={() =>
                  updateStatusMutation.mutate({ status: "submitted" })
                }
                disabled={updateStatusMutation.isPending}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                {t("ratings.page.markSubmitted")}
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("ratings.detail.npsLabel")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.npsScore != null ? (
              <div className="space-y-2">
                <p className="text-4xl font-bold leading-none">
                  {data.npsScore}
                  <span className="ml-1 text-xl text-muted-foreground">
                    /10
                  </span>
                </p>
                {bucketLabel ? (
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                      bucketClass
                    )}
                  >
                    {bucketLabel}
                  </span>
                ) : null}
              </div>
            ) : (
              <p className="text-muted-foreground">—</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("ratings.page.platformRating")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StarRow value={data.platformRating} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("ratings.detail.professionalLabel", {
                name:
                  data.professionalName ?? t("ratings.detail.professionalNone"),
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StarRow value={data.professionalRating} />
          </CardContent>
        </Card>
      </div>

      {data.comment ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("ratings.detail.commentLabel")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
              {data.comment}
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("ratings.detail.requestInfo")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-0">
            <Field label={t("ratings.page.user")}>
              <div className="space-y-0.5">
                <div>
                  {data.consumerName?.trim() || data.consumerEmail || "—"}
                </div>
                {data.consumerName?.trim() && data.consumerEmail ? (
                  <div className="text-xs text-muted-foreground">
                    {data.consumerEmail}
                  </div>
                ) : null}
              </div>
            </Field>
            <Field label={t("ratings.page.type")}>{typeLabel}</Field>
            <Field label={t("ratings.page.product")}>
              {data.productLabel ?? "—"}
            </Field>
            <Field label={t("ratings.detail.professionalLabelShort")}>
              {data.professionalName ?? "—"}
            </Field>
            <Field label={t("ratings.detail.requestLink")}>
              <Link
                to={requestHref}
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                {data.requestId}
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </Field>
            <Field label={t("ratings.page.submittedAt")}>
              {data.submittedAt
                ? formatDisplayDate(data.submittedAt, {
                    includeTime: true,
                    locale: i18n.language,
                  })
                : "—"}
            </Field>
            <Field label={t("ratings.detail.emailSentAt")}>
              {data.emailSentAt
                ? formatDisplayDate(data.emailSentAt, {
                    includeTime: true,
                    locale: i18n.language,
                  })
                : "—"}
            </Field>
            <Field label={t("ratings.page.status")}>
              {displayStatusBadge(deriveDisplayStatus(data))}
            </Field>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

export type { RequestFeedbackRequestType };
